import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
} from "reactstrap";
import Breadcrumbs from "components/Common/BreadCrumbs";
import { urlDecodedId, urlEncodedId } from "components/Common/encodeDecodeFunction";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  showAlert,
  formatSAR,
  ActionButton,
} from "components/Common/commonFunctions";
import NotificationAlert from "react-notification-alert";
import { getStatusColor } from "./QuotationListing";
import { QUOTATION_STATUS_OPTIONS, WORK_ORDER } from "components/Common/constants";
import { downloadQuotationHistory } from "actions/job";
import { getQuotationDetails } from "actions/job";
import CustomModal from "pages/Jobs/CustomModal";
import { handleQuotationStatus } from "./QuotationActions";

function QuotataionDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const decodedId = id ? urlDecodedId(id) : null;
  const dispatch = useDispatch();
  const quotationDetails = useSelector(
    (state) => state.quotation.quotationList,
  );
  const rejectReasonRef = useRef("");
  const notificationAlertRef = useRef(null);
  const [quotationData, setQuotationData] = useState(null);
  const [isAPiProcessing, setIsAPiProcessing] = useState(false);
  const [selectedPDF, setSelectedPDF] = useState(null);
  const [quotationListState, setQuotationListState] = useState({
    loader: false,
    error: "",
    noRecords: false,
  });
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (!decodedId) return;

    setQuotationListState({
      loader: true,
      error: "",
      noRecords: false,
    });

    getQuotationDetails(dispatch, decodedId);
  }, [decodedId, dispatch]);

  useEffect(() => {
    if (!quotationDetails) return;
    const res = quotationDetails;

    if (res.code === 404) {
      setQuotationData(null);
      setQuotationListState({
        loader: false,
        error: "",
        noRecords: true,
      });
      return;
    }

    if (res.code !== 200) {
      setQuotationData(null);
      setQuotationListState({
        loader: false,
        error: res?.message || "Failed to fetch quotation details.",
        noRecords: false,
      });
      return;
    }

    setQuotationData(res.data);
    setQuotationListState({
      loader: false,
      error: "",
      noRecords: false,
    });
  }, [quotationDetails]);

  const jobID = quotationData?.jobID;
  const quotationId = quotationData?.quotationID;

  const quotationItems = [
    ...(quotationData?.quotationItems?.spareParts || []).map((item) => ({
      ...item,
      itemType: "Spare Part",
      itemName: item.itemNumber,
    })),
    ...(quotationData?.quotationItems?.services || []).map((item) => ({
      ...item,
      itemType: "Service",
      itemName: "Service",
    })),
  ];

  const notify = (type, message) => {
    let finalMessage = message;

    if (Array.isArray(message)) {
      finalMessage = message[0]?.Msg || "Something went wrong";
    } else if (typeof message === "object") {
      finalMessage = message?.Msg || "Something went wrong";
    }
    const options = {
      place: "tr",
      message: <div>{finalMessage}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef?.current?.notificationAlert(options);
  };

  const handleFile = (type, id) => {
    switch (type) {
      case "quotation":
        return downloadQuotationHistoryFile(id);

      case "viewQuot":
        return viewQuotationFile(id);

      default:
        return;
    }
  };

  //view quotation
  const viewQuotationFile = async (item) => {
    setIsAPiProcessing(true);
    setSelectedPDF(null);

    try {
      const res = await downloadQuotationHistory(item?.quotationID || item);

      if (res.status === 200 && res.data) {
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);

        setSelectedPDF(url);
      } else {
        showAlert({
          type: "warning",
          title: "Unable to Open",
          message: "No quotation found.",
          showCancel: false,
          confirmText: "OK",
        });
      }
    } catch (err) {
      showAlert({
        type: "warning",
        title: "Unable to Open",
        message: "No quotation found.",
        showCancel: false,
        confirmText: "OK",
      });
    }

    setIsAPiProcessing(false);
  };

  //download quotation
  const downloadQuotationHistoryFile = async (item) => {
    setIsDownloading(true);
    try {
      const res = await downloadQuotationHistory(item?.quotationID || item);

      if (res.status === 200 && res.data) {
        const version = item?.version ?? quotationDetails?.data?.version ?? "0";
        const baseName =
          version !== 0
            ? `quotation-${decodedId}-${version}`
            : `quotation-${decodedId}`;
        const fileName =
          res.headers["content-disposition"]?.match(/filename="(.+?)"/)?.[1] ||
          `${baseName}.pdf`;
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
        showAlert({
          type: "success",
          title: "Download Successful",
          message: <span>Quotation downloaded successfully.</span>,
          showCancel: false,
          confirmText: "OK",
          confirmStyle: "success",
        });
      } else {
        showAlert({
          type: "warning",
          title: "Download Failed",
          message: "No file found.",
          showCancel: false,
          confirmText: "OK",
        });
      }
    } catch (err) {
      showAlert({
        type: "warning",
        title: "Download Failed",
        message: "No file found",
        showCancel: false,
        confirmText: "OK",
      });
    }
    setIsDownloading(false);
  };

  const statusLabel =
    QUOTATION_STATUS_OPTIONS.find(
      (item) => item.value === quotationData?.quotationStatus,
    )?.label || "-";

  const jobDetailsNavigate = (jobID) => {
    const encodedId = urlEncodedId(jobID);
    navigate(`/customer/jobs/${encodedId}/details`);
  };

  return (
    <>
      {isDownloading && (
        <div className="loading-overlay">
          <div className="loading-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-3" />
            <div className="font-weight-bold text-dark">
              Downloading… Please wait.
            </div>
          </div>
        </div>
      )}

      {isAPiProcessing && (
        <div className="loading-overlay">
          <div className="loading-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-3" />
            <div className="font-weight-bold text-dark">
              Processing… Please wait.
            </div>
          </div>
        </div>
      )}
      <Container fluid className="mt-4">
        <div className="rna-container">
          <NotificationAlert ref={notificationAlertRef} />
        </div>

        <div className="customer-card mb-3">
          <Row className="align-items-center">
            <Col md="10" sm="8" xs="12">
              <Breadcrumbs
                items={[
                  {
                    name: "Quotations",
                    link: "/customer/quotations",
                  },
                  {
                    name: `Outlet: ${quotationData?.customerLocation?.outletName || "-"}`,
                  },
                ]}
              />
            </Col>

            <Col md="2" sm="4" xs="12">
              <div className="job-id info-row">
                <i className="fa fa-file-invoice-dollar text-muted mr-1" />
                <span>
                  Quotation ID:&nbsp;
                  {quotationData?.quotationID || "-"}
                </span>
              </div>
            </Col>
          </Row>

          <Row className="info-row">
            <Col md="5" sm="12" className="mt-2">
              <i className="fa fa-briefcase text-muted mr-2" />
              <span>{WORK_ORDER} No:</span>
              <span className="cell-link" onClick={(e) => {
                e.stopPropagation();
                jobDetailsNavigate(quotationData?.jobID)
              }}>{quotationData?.jobID || "-"}</span>

            </Col>

            <Col md="5" sm="6" className="mt-2">
              <i className="fa fa-credit-card text-muted mr-2" />
              <span>Payment:</span>{" "}
              <span>{quotationData?.clientPaymentType || "-"}</span>
            </Col>

            <Col md="2" sm="6" className="mt-2">
              <span>Status:</span>{" "}
              <span
                className="px-2 py-1 rounded-pill"
                style={getStatusColor(quotationData?.quotationStatus)}
              >
                {statusLabel}
              </span>
            </Col>
          </Row>
        </div>

        <Row>
          <Col md="12">
            <Card className="shadow-sm border-0 mt-3">
              {quotationListState.loader ? (
                <div className="loaderContainer">
                  <i className="fa fa-spinner fa-spin fa-2xl loader" />
                </div>
              ) : quotationListState.noRecords ? (
                <div className="loaderContainer text-center py-5">
                  <h5 className="text-muted">No quotation data available.</h5>
                </div>
              ) : quotationListState.error ? (
                <div className="loaderContainer text-center py-5">
                  <h5 className="text-danger">{quotationListState.error}</h5>
                </div>
              ) : (
                <CardBody>
                  <Row className="align-items-center mb-2">
                    <Col md="6">
                      <h4 className="text-deep-blue mb-0">Quotation Items</h4>
                    </Col>

                    <Col
                      md="6"
                      className="d-flex justify-content-md-end justify-content-end flex-wrap mt-3 mt-md-0"
                    >
                      {quotationData?.quotationStatus ===
                        "QUOTATION_REQUESTED_FOR_APPROVAL" && (
                          <>
                            <ActionButton
                              color="danger"
                              icon="fa-times"
                              label="Reject"
                              onClick={() =>
                                handleQuotationStatus({
                                  type: "reject",
                                  quotationId: quotationData?.quotationID,
                                  jobId: jobID,
                                  notify,
                                  setIsAPiProcessing,
                                  rejectReasonRef,
                                  onSuccess: () => getQuotationDetails(dispatch, decodedId),
                                })
                              }
                            />

                            <ActionButton
                              icon="fa-check"
                              label="Accept"
                              onClick={() =>
                                handleQuotationStatus({
                                  type: "accept",
                                  quotationId: quotationData?.quotationID,
                                  jobId: jobID,
                                  notify,
                                  setIsAPiProcessing,
                                  rejectReasonRef,
                                  onSuccess: () => getQuotationDetails(dispatch, decodedId),
                                })
                              }
                            />
                          </>
                        )}

                      <ActionButton
                        icon="fa-eye"
                        tooltip="View Quotation"
                        onClick={() =>
                          handleFile("viewQuot", quotationData?.quotationID)
                        }
                        circle
                      />

                      <ActionButton
                        icon="fa-download"
                        tooltip="Download Quotation"
                        onClick={() =>
                          handleFile("quotation", quotationData?.quotationID)
                        }
                        circle
                      />
                    </Col>
                  </Row>
                  <div className="border-bottom border-muted mb-1" />

                  <Row>
                    {/* Items */}
                    <Col xl="8" lg="7" md="12" className="mb-4">
                      <Card className="shadow-sm border-0 quotation-table-card">
                        <div className="table-responsive">
                          <CardBody className="p-0">
                            <Table
                              responsive
                              hover
                              className="quotation-table mb-0"
                            >
                              <thead>
                                <tr>
                                  <th width="25%">Item</th>
                                  <th width="25%">Description</th>
                                  <th className="text-center" width="10%">
                                    Qty
                                  </th>
                                  <th className="text-right" width="20%">
                                    Unit Price
                                  </th>
                                  <th className="text-right" width="20%">
                                    Total
                                  </th>
                                </tr>
                              </thead>

                              <tbody>
                                {quotationItems.length ? (
                                  quotationItems.map((item) => (
                                    <tr key={`${item.itemType}-${item.ID}`}>
                                      <td className="text-muted">
                                        {item.itemName}
                                      </td>

                                      <td className="text-muted">
                                        {item.description || "-"}
                                      </td>

                                      <td className="text-center">
                                        {item.quantity}
                                      </td>

                                      <td className="text-right">
                                        SAR {formatSAR(item.unitPrice)}
                                      </td>

                                      <td className="text-right font-weight-bold">
                                        SAR {formatSAR(item.totalPrice)}
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td
                                      colSpan="6"
                                      className="text-center py-5"
                                    >
                                      No quotation items found.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </Table>
                          </CardBody>
                        </div>
                      </Card>
                    </Col>

                    <Col xl="4" lg="5" md="12">
                      <Card className="quotation-summary-card shadow-sm border-0">
                        <CardBody>
                          <h5 className="summary-title text-muted">
                            Price Summary
                          </h5>

                          <div className="summary-item">
                            <span>Spare Parts Total</span>
                            <span>
                              SAR{" "}
                              {formatSAR(quotationData?.sparePartTotal || 0)}
                            </span>
                          </div>

                          <div className="summary-item">
                            <span>Service Total</span>
                            <span>
                              SAR {formatSAR(quotationData?.serviceTotal || 0)}
                            </span>
                          </div>

                          <div className="summary-item">
                            <span>Subtotal</span>
                            <span>
                              SAR {formatSAR(quotationData?.subtotal || 0)}
                            </span>
                          </div>

                          <div className="summary-item">
                            <span>
                              Discount (
                              {(quotationData?.discountPercentage || 0).toFixed(
                                2,
                              )}
                              %)
                            </span>
                            <span>
                              - SAR {formatSAR(quotationData?.discount || 0)}
                            </span>
                          </div>

                          <div className="summary-item">
                            <span>Discounted Total</span>
                            <span>
                              SAR{" "}
                              {formatSAR(quotationData?.discountedTotal || 0)}
                            </span>
                          </div>

                          <div className="summary-item">
                            <span>VAT ({quotationData?.vat || 0}%)</span>
                            <span>
                              SAR {formatSAR(quotationData?.vatAmount || 0)}
                            </span>
                          </div>

                          <hr className="my-3 border-top border-2 border-secondary-subtle" />

                          <div className="summary-total font-weight-bold">
                            <span>Grand Total</span>
                            <span>
                              SAR {formatSAR(quotationData?.grandTotal || 0)}
                            </span>
                          </div>

                          <div className="summary-payable font-weight-bold">
                            <span>Payable Amount</span>
                            <span>
                              SAR {formatSAR(quotationData?.payableAmount || 0)}
                            </span>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </CardBody>
              )}
            </Card>
          </Col>
        </Row>
      </Container>
      <CustomModal
        isOpen={!!selectedPDF}
        onClose={() => setSelectedPDF(null)}
        title={"Document Preview"}
      >
        <div className="pdf-container">
          <iframe src={selectedPDF} title="PDF Viewer" className="pdf-iframe" />
        </div>
      </CustomModal>
    </>
  );
}

export default QuotataionDetails;
